import { Eye, Layers, X } from 'lucide-react';
import { JiraIcon } from './IntegrationIcons';
import type { JiraConnection } from './useJiraConnection';
import type { PriorityExportRecord } from './integrationTypes';

export function JiraExportModal({
  jira,
  priorities,
}: {
  jira: JiraConnection;
  priorities: PriorityExportRecord[];
}) {
  const {
    showExportJira,
    setShowExportJira,
    showHierarchyPreview,
    selectedJiraProject,
    setSelectedJiraProject,
    jiraProjects,
    preserveHierarchy,
    setPreserveHierarchy,
    selectedPriorities,
    selectAllPriorities,
    togglePrioritySelection,
    handleJiraExportPreview,
  } = jira;

  return (
    <>
      {/* Export to Jira Modal */}
      {showExportJira && !showHierarchyPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--bg-raised)] shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-[var(--rule)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[var(--slate-dim)] text-[var(--text-sec)]">
                  <JiraIcon />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-pri)]">Export to Jira</h2>
                  <p className="text-sm text-[var(--text-ter)]">Select project and priorities to export</p>
                </div>
              </div>
              <button onClick={() => setShowExportJira(false)} className="p-2 hover:bg-[var(--hover-tint)]">
                <X className="h-5 w-5 text-[var(--text-ter)]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Project Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Select Project</label>
                <select
                  value={selectedJiraProject}
                  onChange={(e) => setSelectedJiraProject(e.target.value)}
                  className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose a project...</option>
                  {jiraProjects.map(project => (
                    <option key={project.key} value={project.key}>
                      {project.key} - {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Hierarchy Option */}
              <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-3 mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preserveHierarchy}
                    onChange={(e) => setPreserveHierarchy(e.target.checked)}
                    className="rounded border-[var(--rule)] text-[var(--text-sec)] focus:ring-purple-500"
                  />
                  <div>
                    <span className="font-medium text-[var(--text-pri)] flex items-center gap-2">
                      <Layers className="h-4 w-4 text-[var(--text-ter)]" />
                      Preserve Hierarchy
                    </span>
                    <p className="text-xs text-[var(--text-sec)] mt-0.5">
                      Group by category: Epic → Stories
                    </p>
                  </div>
                </label>
              </div>

              {/* Priorities List */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[var(--text-sec)]">{priorities.length} priorities available</span>
                <button
                  onClick={selectAllPriorities}
                  className="text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
                >
                  Select All
                </button>
              </div>
              <div className="space-y-2">
                {priorities.map((priority) => (
                  <label
                    key={priority.id}
                    className={`flex items-start gap-3 p-3  border cursor-pointer transition-colors ${
                      selectedPriorities.includes(priority.id)
                        ? 'border-[var(--slate)] bg-[var(--slate-dim)]'
                        : 'border-[var(--rule)] hover:bg-[var(--surface)]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPriorities.includes(priority.id)}
                      onChange={() => togglePrioritySelection(priority.id)}
                      className="mt-1 rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[var(--text-ter)]">#{priority.rank}</span>
                        <span className="font-medium text-[var(--text-pri)]">{priority.title}</span>
                      </div>
                      {priority.description != null && String(priority.description) !== '' && (
                        <p className="text-xs text-[var(--text-ter)] mt-1 line-clamp-1">{String(priority.description)}</p>
                      )}
                    </div>
                    {priority.category != null && String(priority.category) !== '' && (
                      <span className="text-xs px-2 py-1 bg-[var(--hover-tint)] rounded text-[var(--text-sec)]">
                        {String(priority.category)}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div className="p-4 border-t border-[var(--rule)] flex items-center justify-between bg-[var(--surface)]">
              <span className="text-sm text-[var(--text-sec)]">
                {selectedPriorities.length} priority(ies) selected
              </span>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowExportJira(false)}
                  className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleJiraExportPreview}
                  disabled={selectedPriorities.length === 0 || !selectedJiraProject}
                  className="px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Preview Hierarchy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
